import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { api } from "./api";
import { isCompleteDay } from "utils/completeDay";

// Shared default: TODAY in Cairo TZ. The financial workflow is "open the
// dashboard, set a price for the day I just lived through". A "yesterday"
// default trained the user to type prices on a row labeled with the
// previous calendar day — so the price they set for "today's leads"
// landed on yesterday's row. Defaulting to today removes that mismatch:
// the row you see when the dashboard opens is the calendar day you're
// currently working on.
const todayCairoIso = () => {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Cairo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
};

const defaultDate = todayCairoIso();

// Sticky persistence for the shared financial date range so the operator's
// chosen window survives a full reload (not just in-tab navigation).
const FIN_SINCE_KEY = "ft.fin.since";
const FIN_UNTIL_KEY = "ft.fin.until";
const readStored = (key, fallback) => {
  try {
    const v = window.localStorage.getItem(key);
    // A half-typed year saved by an older build ("0002-10-04") must not come
    // back as the range after a reload.
    return isCompleteDay(v) ? v : fallback;
  } catch {
    return fallback;
  }
};
const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // best-effort — ignore quota / private-mode failures
  }
};

const initialState = {
  mode: "dark",
  // Date range shared across the financial pages: Financial Dashboard,
  // Mappings, Partners, Activity Audit. Setting it on any one of them
  // updates the others. Persisted to localStorage so it survives a full
  // reload, not just in-tab navigation.
  financialSince: readStored(FIN_SINCE_KEY, defaultDate),
  financialUntil: readStored(FIN_UNTIL_KEY, defaultDate),
  // Client-clock timestamp (ms) of the most recent financial-INPUT edit the
  // operator made this session — pricing, mappings, splits, project pins,
  // carry-forward, etc. The Budget Tracker compares this against when its own
  // data was last fetched (the query's fulfilledTimeStamp, also a client
  // clock — so no server/client skew) to warn that the numbers on screen
  // predate a recent edit and a manual Refresh is needed. The server snapshot
  // is rebuilt within seconds of any edit; this only drives the advisory
  // hint, never a fetch. null = no edit made yet this session.
  lastFinancialEditAt: null,
};

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setMode: (state, action) => {
      if (action.payload) {
        state.mode = action.payload;
      } else {
        state.mode = state.mode === "light" ? "dark" : "light";
      }
    },
    setFinancialDateRange: (state, action) => {
      // The date boxes report every keystroke ("0002-10-04", "0020-…",
      // "20202-…" while a year is typed). Only a finished day between
      // DAY_MIN and DAY_MAX becomes the range — anything else is ignored, so
      // it never reaches localStorage or the queries on any financial page.
      const { since, until } = action.payload || {};
      if (isCompleteDay(since)) {
        state.financialSince = since;
        writeStored(FIN_SINCE_KEY, since);
      }
      if (isCompleteDay(until)) {
        state.financialUntil = until;
        writeStored(FIN_UNTIL_KEY, until);
      }
    },
  },
  // Stamp lastFinancialEditAt whenever a financial-input mutation that the
  // Budget Tracker reflects succeeds. These are exactly the edits that do NOT
  // auto-invalidate the FinancialBudgetTracker cache (deposits / role /
  // project-pricing already do, so they refetch and never need the hint).
  // Date.now() here is deliberate: it's an advisory UI timestamp, not derived
  // state, and it must be a client clock to compare cleanly against the
  // query's client-side fulfilledTimeStamp.
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        api.endpoints.setFinancialMapping.matchFulfilled,
        api.endpoints.deleteFinancialMapping.matchFulfilled,
        api.endpoints.setCampaignPastFix.matchFulfilled,
        api.endpoints.setCampaignProjectPin.matchFulfilled,
        api.endpoints.updateFinancialClientPricing.matchFulfilled,
        api.endpoints.clearFinancialCache.matchFulfilled,
        api.endpoints.setClientDailyPricing.matchFulfilled,
        api.endpoints.deleteClientDailyPricing.matchFulfilled,
        api.endpoints.bulkSetClientDailyPricing.matchFulfilled,
        api.endpoints.setClientSpendSplit.matchFulfilled,
        api.endpoints.deleteClientSpendSplit.matchFulfilled,
        api.endpoints.carryForwardClientPricing.matchFulfilled,
        api.endpoints.carryForwardAllPricing.matchFulfilled,
        api.endpoints.updateClientAliases.matchFulfilled,
        api.endpoints.applyPricingSuggestions.matchFulfilled,
      ),
      (state) => {
        state.lastFinancialEditAt = Date.now();
      },
    );
  },
});

export const { setMode, setFinancialDateRange } = globalSlice.actions;

export default globalSlice.reducer;
